import axios from "axios";

import { getRoot } from "./helpers";

const ORDER_NUMBER_KEY = "eziceportal_orderNumber";
const LAST_NAME_KEY = "eziceportal_lastName";
const TOKEN_KEY = "eziceportal_accessToken";

export function getStoredCredentials() {
  return {
    orderNumber: localStorage.getItem(ORDER_NUMBER_KEY) || "",
    lastName: localStorage.getItem(LAST_NAME_KEY) || "",
  };
}

export function storeSession({ orderNumber, lastName, accessToken }) {
  localStorage.setItem(ORDER_NUMBER_KEY, orderNumber);
  localStorage.setItem(LAST_NAME_KEY, lastName);
  localStorage.setItem(TOKEN_KEY, accessToken || "");
}

export function clearSession() {
  localStorage.setItem(ORDER_NUMBER_KEY, "");
  localStorage.setItem(LAST_NAME_KEY, "");
  localStorage.setItem(TOKEN_KEY, "");
}

// Looking the order up by number and last name is what proves ownership; the
// token it returns is what the rest of the portal's requests carry.
export async function lookupOrder(orderNumber, lastName) {
  const cleanedOrderNumber = orderNumber.replaceAll("#", "");

  const { data } = await axios.get(
    `${getRoot()}/api/orders/${cleanedOrderNumber}?last_name=${encodeURIComponent(
      lastName
    )}`
  );

  storeSession({
    orderNumber: cleanedOrderNumber,
    lastName,
    accessToken: data.access_token,
  });

  return data;
}

export async function lookupOrderByEmail(email) {
  const { data } = await axios.get(
    `${getRoot()}/api/orders/order_by_email?email=${encodeURIComponent(email)}`
  );

  storeSession({
    orderNumber: data.cust_ref.replaceAll("#", ""),
    lastName: data.cust_last_name,
    accessToken: data.access_token,
  });

  return data;
}

// A failed lookup answers 404 rather than 401, so this can never bounce off
// its own retry.
async function refreshAccessToken() {
  const { orderNumber, lastName } = getStoredCredentials();

  if (!orderNumber || !lastName) {
    return null;
  }

  try {
    const { access_token } = await lookupOrder(orderNumber, lastName);

    return access_token;
  } catch (e) {
    clearSession();

    return null;
  }
}

export function installOrderAccessInterceptors() {
  axios.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (token) {
      config.headers["X-Order-Access-Token"] = token;
    }

    return config;
  });

  // The credentials live in local storage, so an expired token costs a silent
  // re-lookup rather than sending the customer back to the login screen.
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config, response } = error;

      if (
        response?.status !== 401 ||
        response?.data?.code !== "order_token_invalid" ||
        config?.hasRetriedWithFreshToken
      ) {
        return Promise.reject(error);
      }

      const token = await refreshAccessToken();

      if (!token) {
        return Promise.reject(error);
      }

      config.hasRetriedWithFreshToken = true;
      config.headers["X-Order-Access-Token"] = token;

      return axios(config);
    }
  );
}
